/** The External access search namespaces.
 *
 * External_Access_Log is monthly partitioned and kept 13 months, so a `created` time window
 * is MANDATORY in every search (the page gates the query client-side; the backend 400s) —
 * the same rule as /api-requests. `partner` / `tenant` take a code (iexact; an all-digits
 * value also matches the id), `credential` / `user` take ids; the enum namespaces
 * (`principal`, `channel`, `outcome`) take the stored codes.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (ExternalAccessFilterNode — contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';
import { makePartnerSuggestionFetcher } from '@/api/partners';

import {
  CHANNEL_LABELS,
  CHANNELS,
  METHODS,
  OUTCOME_LABELS,
  OUTCOMES,
  PRINCIPAL_KINDS,
  PRINCIPAL_LABELS,
} from './externalAccessFormat';

/** A local (no-API) suggestion fetcher over fixed codes, each with its label. */
function localCodes(codes: readonly string[], labels: Record<string, string> = {}) {
  return (query: string) => {
    const q = query.toLowerCase();
    return Promise.resolve(
      codes
        .filter((c) => c.toLowerCase().includes(q) || (labels[c] ?? '').toLowerCase().includes(q))
        .map((c) => ({ value: c, label: c, ...(labels[c] ? { description: labels[c] } : {}) })),
    );
  };
}

export function buildExternalAccessNamespaces(): NamespaceConfigMap {
  return {
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the call was made, UTC. `=` matches a whole day; a range takes date-time bounds. MANDATORY — every search must carry a created window (it selects the monthly partitions to read).',
      examples: ['created=2026-10-14', 'created="2026-10-14 09:00".."2026-10-15 18:00"'],
    },
    partner: {
      label: 'partner',
      operators: ['iexact', 'exact'],
      valueType: 'string',
      fetchSuggestions: makePartnerSuggestionFetcher(),
      help: 'Integration partner by code; an all-digits value also matches the partner id, `=#` pins one partner by id.',
      examples: ['partner=acme', 'partner=#5'],
    },
    credential: {
      label: 'credential',
      operators: ['exact'],
      valueType: 'string',
      help: 'API credential id (exact) — the key the call authenticated with, or tried to.',
      examples: ['credential=123'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) the call acted on, by code; an all-digits value also matches the tenant id. A partner sandbox is a tenant too.',
      examples: ['tenant=acmesbx', 'tenant=42'],
    },
    user: {
      label: 'user',
      operators: ['exact'],
      valueType: 'string',
      help: 'OAuth user id (exact) — calls made through an OAuth grant (MCP, the developer portal).',
      examples: ['user=1234'],
    },
    principal: {
      label: 'principal',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localCodes(PRINCIPAL_KINDS, PRINCIPAL_LABELS),
      help: 'Who called: `api_key` (a sandbox or partner key), `oauth_user` (an OAuth grant) or `ventrata_token`.',
      examples: ['principal=api_key', 'principal=oauth_user'],
    },
    channel: {
      label: 'channel',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localCodes(CHANNELS, CHANNEL_LABELS),
      help: 'Where the call came in: `api`, `mcp`, `docs` or `portal` (the developer portal).',
      examples: ['channel=mcp', 'channel=api'],
    },
    outcome: {
      label: 'outcome',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localCodes(OUTCOMES, OUTCOME_LABELS),
      help: 'How it ended: `ok`, `client_error`, `server_error`, `denied` (the key or grant was refused) or `throttled`.',
      examples: ['outcome=denied', '!outcome=ok'],
    },
    status: {
      label: 'status',
      operators: ['exact', 'gt', 'lt', 'range'],
      valueType: 'string',
      help: 'Response HTTP status code. Open-ended ranges give greater/less than: `500..` (≥500), `..400` (<400), `400..499` (4xx).',
      examples: ['status=429', 'status=500..', 'status=400..499'],
    },
    operation: {
      label: 'operation',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Route name, MCP tool or doc path (`=` equals, `=~` contains).',
      examples: ['operation=~import', 'operation=list_bookings'],
    },
    method: {
      label: 'method',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localCodes(METHODS),
      help: 'HTTP method.',
      examples: ['method=POST'],
    },
    error_code: {
      label: 'error_code',
      operators: ['iexact'],
      valueType: 'string',
      help: 'The machine-readable error code the call was answered with (equals, case-insensitive).',
      examples: ['error_code=rate_limited', 'error_code=sandbox_suspended'],
    },
    request: {
      label: 'request',
      operators: ['exact'],
      valueType: 'string',
      help: 'Request ID assigned by the platform (exact) — the id in the logs and in the error the caller saw.',
      examples: ['request=abcd1234efgh'],
    },
    ip: {
      label: 'ip',
      operators: ['exact'],
      valueType: 'string',
      help: 'Client IP address (exact).',
      examples: ['ip=203.0.113.7'],
    },
    client: {
      label: 'client',
      operators: ['icontains'],
      valueType: 'string',
      help: 'The calling client: the MCP client name or the OAuth client id (contains).',
      examples: ['client=~claude'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'Access-log row id (exact).',
      examples: ['id=123456'],
    },
  };
}
